import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getPoojariById } from '../../services/poojariService';
import { getReviewsByPoojari, getRatingSummary } from '../../services/reviewService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatPaise } from '../../utils/money';

const normalizePoojariResponse = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
        return payload.data;
    }

    if (payload.content && typeof payload.content === 'object' && !Array.isArray(payload.content)) {
        return payload.content;
    }

    if (payload.result && typeof payload.result === 'object' && !Array.isArray(payload.result)) {
        return payload.result;
    }

    return payload;
};

const PoojariProfile = () => {
    const { id } = useParams();
    const [poojari, setPoojari] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const { token } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchPoojariDetails();
    }, [id]);

    const fetchPoojariDetails = async () => {
        try {
            const poojariResponse = await getPoojariById(id);
            const normalizedPoojari = normalizePoojariResponse(poojariResponse);
            setPoojari(normalizedPoojari);

            if (token) {
                const reviewData = await getReviewsByPoojari(id);
                setReviews(reviewData);
                const summaryData = await getRatingSummary(id);
                setSummary(summaryData);
            }
        } catch (error) {
            toast.error('Error fetching poojari details');
        } finally {
            setLoading(false);
        }
    };

    const handleBookNow = () => {
        if (!token) {
            toast.warning('Please login to book a poojari');
            navigate('/login');
            return;
        }
        navigate(`/book/${id}`);
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: '#8B4513' }}>
            </div>
        </div>
    );

    if (!poojari) return (
        <div className="text-center py-5">
            <h4>Poojari not found</h4>
        </div>
    );

    return (
        <div className="container py-4">
            <div className="row g-4">
                {/* Left Column - Poojari Info */}
                <div className="col-md-4">
                    <div className="card shadow-sm sticky-top"
                        style={{ top: '20px' }}>
                        <div className="card-body text-center p-4">
                            <div className="display-1 mb-3">🙏</div>
                            <h3 className="fw-bold">
                                {poojari.firstName} {poojari.lastName}
                            </h3>
                            <p className="text-muted">
                                📍 {poojari.city}, {poojari.state}
                            </p>
                            <div className="d-flex justify-content-center gap-3 mb-3">
                                <div>
                                    <h5 className="mb-0">⭐ {poojari.rating}</h5>
                                    <small>Rating</small>
                                </div>
                                <div>
                                    <h5 className="mb-0">{poojari.experienceYears}</h5>
                                    <small>Years Exp</small>
                                </div>
                                <div>
                                    <h5 className="mb-0">{poojari.totalPoojasCompleted}</h5>
                                    <small>Poojas Done</small>
                                </div>
                            </div>
                            <h4 className="text-success fw-bold">
                                {formatPaise(poojari.chargePerPooja)}
                            </h4>
                            <p className="text-muted">per pooja</p>
                            {poojari.isAvailable ? (
                                <span className="badge bg-success mb-3">
                                    ✅ Available
                                </span>
                            ) : (
                                <span className="badge bg-danger mb-3">
                                    ❌ Not Available
                                </span>
                            )}
                            <button
                                className="btn w-100 text-white fw-bold"
                                style={{ backgroundColor: '#8B4513' }}
                                onClick={handleBookNow}
                                disabled={!poojari.isAvailable}>
                                {poojari.isAvailable
                                    ? '📅 Book Now'
                                    : 'Not Available'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column - Details */}
                <div className="col-md-8">
                    {/* About */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header fw-bold"
                            style={{ backgroundColor: '#FFF8F0' }}>
                            About
                        </div>
                        <div className="card-body">
                            <p>{poojari.profileDescription
                                || 'No description available'}</p>
                        </div>
                    </div>

                    {/* Specializations */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header fw-bold"
                            style={{ backgroundColor: '#FFF8F0' }}>
                            Specializations
                        </div>
                        <div className="card-body">
                            {poojari.specializations?.map((spec, i) => (
                                <span key={i}
                                    className="badge me-2 mb-2 p-2"
                                    style={{ backgroundColor: '#8B4513' }}>
                                    {spec}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Languages */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header fw-bold"
                            style={{ backgroundColor: '#FFF8F0' }}>
                            Languages Known
                        </div>
                        <div className="card-body">
                            {poojari.languagesKnown?.map((lang, i) => (
                                <span key={i}
                                    className="badge bg-secondary me-2 mb-2 p-2">
                                    {lang}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Rating Summary */}
                    {summary && (
                        <div className="card shadow-sm mb-4">
                            <div className="card-header fw-bold"
                                style={{ backgroundColor: '#FFF8F0' }}>
                                Rating Summary
                            </div>
                            <div className="card-body">
                                <div className="row text-center">
                                    {[5, 4, 3, 2, 1].map(star => (
                                        <div key={star} className="col">
                                            <h5>{star}⭐</h5>
                                            <p>{star === 5
                                                ? summary.fiveStarCount
                                                : star === 4
                                                ? summary.fourStarCount
                                                : star === 3
                                                ? summary.threeStarCount
                                                : star === 2
                                                ? summary.twoStarCount
                                                : summary.oneStarCount}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reviews */}
                    <div className="card shadow-sm">
                        <div className="card-header fw-bold"
                            style={{ backgroundColor: '#FFF8F0' }}>
                            Reviews ({reviews.length})
                        </div>
                        <div className="card-body">
                            {reviews.length === 0 ? (
                                <p className="text-muted">
                                    No reviews yet
                                </p>
                            ) : (
                                reviews.map(review => (
                                    <div key={review.id}
                                        className="border-bottom pb-3 mb-3">
                                        <div className="d-flex justify-content-between">
                                            <strong>
                                                User #{review.userId}
                                            </strong>
                                            <span>
                                                {'⭐'.repeat(review.rating)}
                                            </span>
                                        </div>
                                        <h6>{review.title}</h6>
                                        <p className="text-muted mb-0">
                                            {review.comment}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PoojariProfile;